import React, { FC, useContext, useRef, useState, useEffect } from "react";
import { graphql, Link } from "gatsby";
import { WavySection } from "../components/wavy-section";
import {
  colors,
  ySpaceRem,
  xSpaceRem,
  fontStacks,
  targetBaseFontSizePx,
} from "../global-style";
import { LayoutContext } from "../components/layout";
import { Center } from "../components/center";
import { TipsPageQuery } from "../../graphql-types";
import { Grid } from "../components/grid";
import { Footer } from "../components/footer";
import { css } from "styled-components";
import { SEO } from "../components/seo";
import { TipPreview } from "../components/tip-preview";
import { intersection } from "lodash";

export const query = graphql`
  query TipsPage {
    wp {
      page(id: "tips/", idType: URI) {
        seo {
          ...SEO
        }
      }

      tips(
        first: 99999
        where: { orderby: { field: MENU_ORDER, order: ASC } }
      ) {
        edges {
          node {
            ...Tip
          }
        }
      }

      characteristics {
        edges {
          node {
            title
            characteristicCustomFields {
              isEffect
              icon {
                sourceUrl
                localFile {
                  publicURL
                }
              }
            }
          }
        }
      }
    }
  }
`;

const TipPage: FC<{ data: TipsPageQuery }> = props => {
  const { remInPx, waveClearance } = useContext(LayoutContext);
  const pageTitleRef = useRef<HTMLHeadingElement>(null);

  const [characteristicFilter, setCharacteristicFilter] = useState<string[]>(
    []
  );

  useEffect(() => {
    if (
      characteristicFilter.length ===
      props.data.wp.characteristics?.edges?.length
    ) {
      setCharacteristicFilter([]);
    }
  }, [characteristicFilter]);

  return (
    <>
      <SEO {...props.data.wp.page?.seo} />

      <WavySection
        backgroundColor={colors.mediumBlue}
        style={{
          color: colors.white,
          paddingTop: `calc(${waveClearance} + ${2 * ySpaceRem}rem)`,
          paddingBottom: `${ySpaceRem}rem`,
          transition: "padding 0.25s ease",
        }}
      >
        <Center>
          <div style={{ fontSize: "1.1rem" }}>
            <Link to="/" style={{ textDecoration: "underline" }}>
              Home
            </Link>{" "}
            > Tips
          </div>
        </Center>
      </WavySection>

      <div
        style={{
          paddingTop: `${1.5 * ySpaceRem}rem`,
          paddingBottom: `calc(${1.5 * ySpaceRem}rem + ${waveClearance})`,
        }}
      >
        <Center>
          <div style={{ maxWidth: `${32 * xSpaceRem}rem` }}>
            <div
              style={{
                ...(pageTitleRef.current &&
                  (() => {
                    const paddingYRem =
                      ySpaceRem / 2 -
                      ((pageTitleRef.current.getBoundingClientRect().height /
                        remInPx) %
                        (ySpaceRem / 2));

                    return {
                      paddingTop: `${0.5 * paddingYRem}rem`,
                      paddingBottom: `${0.5 * paddingYRem}rem`,
                    };
                  })()),
                transition: "padding 0.25s ease",
                marginBottom: `${0.5 * ySpaceRem}rem`,
              }}
            >
              <h1
                ref={pageTitleRef}
                style={{
                  fontFamily: fontStacks.cubano,
                }}
                css={(() => {
                  const minFontSizeRem = 2;
                  const maxFontSizeRem = 2.8888;
                  const vwRangeStartPx = 500;

                  return css`
                    font-size: ${(minFontSizeRem *
                      (remInPx || targetBaseFontSizePx)) /
                      (vwRangeStartPx / 100)}vw;
                    line-height: 1;

                    @media (max-width: ${vwRangeStartPx}px) {
                      font-size: ${minFontSizeRem}rem;
                    }

                    @media (min-width: ${vwRangeStartPx *
                        (maxFontSizeRem / minFontSizeRem)}px) {
                      font-size: ${maxFontSizeRem}rem;
                    }
                  `;
                })()}
              >
                Tips
              </h1>
            </div>

            <p
              style={{
                fontSize: "1.1rem",
                marginBottom: `${1 * ySpaceRem}rem`,
              }}
            >
              Wilt u zelf aan de slag met een waterrobuuste en klimaatbewuste
              woonomgeving? Bekijk de tips en maatregelen. Zo draagt u zelf
              direct bij aan meer biodiversiteit, het beperken van
              wateroverlast, minder droogte en minder overlast van hitte.
            </p>

            <div
              style={{
                marginBottom: `${1 * ySpaceRem}rem`,
                padding: `${0.5 * ySpaceRem}rem ${0.5 * xSpaceRem}rem`,
                backgroundColor: colors.lightestGrey,
                overflow: "hidden",
              }}
            >
              <h3
                style={{
                  paddingBottom: `${0.5 * ySpaceRem}rem`,
                  textAlign: "center",
                }}
              >
                Filter tips op
              </h3>

              <div
                style={{
                  overflow: "hidden",
                  backgroundColor: colors.white,
                  color: colors.black,
                  padding: `${0.5 * ySpaceRem}rem ${0.5 * xSpaceRem}rem`,
                }}
              >
                <ul
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    margin: `${-0.5 * ySpaceRem}rem ${-0.5 * xSpaceRem}rem`,
                  }}
                >
                  <li
                    style={{
                      margin: `${0.5 * ySpaceRem}rem ${0.5 * xSpaceRem}rem`,
                    }}
                  >
                    <label style={{ display: "flex", alignItems: "center" }}>
                      <input
                        type="checkbox"
                        onChange={() => setCharacteristicFilter([])}
                        checked={
                          characteristicFilter.length === 0 ||
                          characteristicFilter.length ===
                            props.data.wp.characteristics?.edges?.length
                        }
                      />

                      <em style={{ marginLeft: `${0.25 * xSpaceRem}rem` }}>
                        Alle tips
                      </em>
                    </label>
                  </li>

                  {props.data.wp.characteristics?.edges
                    ?.filter(
                      edge => edge?.node?.characteristicCustomFields?.isEffect
                    )
                    .map(edge => (
                      <li
                        style={{
                          margin: `${0.5 * ySpaceRem}rem ${0.5 * xSpaceRem}rem`,
                        }}
                      >
                        <label
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <input
                            type="checkbox"
                            checked={characteristicFilter.includes(
                              edge?.node?.title as string
                            )}
                            onChange={e => {
                              if (e.target.checked) {
                                setCharacteristicFilter([
                                  ...characteristicFilter,
                                  edge?.node?.title as string,
                                ]);
                              } else {
                                setCharacteristicFilter(characteristicFilter =>
                                  characteristicFilter.filter(
                                    characteristic =>
                                      characteristic !== edge?.node?.title
                                  )
                                );
                              }
                            }}
                          />

                          <p
                            style={{
                              marginLeft: `${0.25 * xSpaceRem}rem`,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <img
                              src={
                                edge?.node?.characteristicCustomFields?.icon
                                  ?.localFile?.publicURL || ""
                              }
                              alt=""
                              style={{
                                filter: `grayscale(${
                                  characteristicFilter.length === 0 ||
                                  characteristicFilter.includes(
                                    edge?.node?.title as string
                                  )
                                    ? 0
                                    : 100
                                }%)`,
                                transition: "all 0.25s ease-in-out",
                              }}
                            />

                            <span
                              style={{ marginLeft: `${0.5 * xSpaceRem}rem` }}
                            >
                              {edge?.node?.title}
                            </span>
                          </p>
                        </label>
                      </li>
                    ))}
                </ul>
              </div>
            </div>

            <Grid
              style={{
                marginBottom: `${0.5 * ySpaceRem}rem`,
                gridGap: `${2 * ySpaceRem}rem ${xSpaceRem}rem`,
              }}
            >
              {props.data.wp.tips?.edges
                ?.filter(edge => {
                  return (
                    characteristicFilter.length === 0 ||
                    intersection(
                      characteristicFilter,
                      edge?.node?.tipCustomFields?.characteristicsTips?.map(
                        edge => edge?.title
                      ) as string[]
                    ).length > 0
                  );
                })
                .map(
                  (edge, i) =>
                    edge?.node && <TipPreview key={i} tip={edge.node} />
                )}
            </Grid>
          </div>
        </Center>
      </div>

      <Footer backgroundColor={colors.lightestGrey} />
    </>
  );
};

export default TipPage;
